import type { FC } from 'react';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  FormHelperText,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteDialog,
  Iconify,
  MenuItem,
  MoneyInput,
  TableMoreMenu,
} from '@pflegenavi/web-components';
import type {
  BatchReceiptDetails,
  ReceiptBatchFormValues,
  TaxItem,
} from './ServiceProviderReceiptBatchForm';
import type { FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { ServiceProviderReceiptBatchTaxSelection } from './ServiceProviderReceiptBatchTaxSelection';
import type { LineItemConfigurationResultDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ServiceProviderReceiptBatchServiceSelection } from './ServiceProviderReceiptBatchServiceSelection';
import {
  MAX_ENTRY_TOTAL_AMOUNT_FOR_RECEIPT_GENERATION_IN_CENTS,
  MAX_SERVICE_PROVIDER_STRIPE_PAYMENT_CENT_AMOUNT,
} from '@pflegenavi/shared/constants';
import { createNewLineItem } from './ServiceProviderReceiptBatchReceiptsView';
import type { Gender } from '@pflegenavi/shared/api';
import { ServiceProviderReceiptBatchSelectResident } from './ServiceProviderReceiptBatchSelectResident';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { useTaxItemsAndDefault } from '../useDefaultTaxItem';
import { DatePicker } from '@mui/x-date-pickers';
import { fromZonedTime } from 'date-fns-tz';
import { startOfDay } from 'date-fns';

interface ReceiptBatchRowProps {
  index: number;
  receipt: BatchReceiptDetails;
  individualDatesMode?: boolean;
  setReceiptFieldValue: (field: string, value: any) => void;
  setLineItemFieldValue: (
    field: string,
    lineItemIndex: number,
    value: any
  ) => void;
  setLineItemValues: (
    lineItemIndex: number,
    name: string,
    amount: number | undefined,
    tax: TaxItem | undefined
  ) => void;
  removeRow: (index: number) => void;
  setShownImageId: (imgId: string | undefined) => void;
  setPreviewImageIds: (imgIds: string[]) => void;
  lastInputRef?: React.RefObject<HTMLInputElement | null>;
  showTabTooltip: boolean;
  setShowTabTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  upload: (file: File) => Promise<string | undefined>;
  addImages: (imageIds: string[]) => void;
  lineItemConfigurations: LineItemConfigurationResultDto[] | undefined;
  autoGeneratedReceipts: boolean;
  residents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  isLoadingResidents: boolean;
  enqueueSnackbarOnce: (message: string, options?: any) => void;
}

const rowHeight = 60;

const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,application/pdf';

export const ServiceProviderReceiptBatchRowView: FC<ReceiptBatchRowProps> =
  memo(
    ({
      index,
      receipt,
      individualDatesMode,
      setReceiptFieldValue,
      setLineItemFieldValue,
      setLineItemValues,
      removeRow,
      setShownImageId,
      setPreviewImageIds,
      lastInputRef,
      showTabTooltip,
      setShowTabTooltip,
      upload,
      addImages,
      lineItemConfigurations,
      autoGeneratedReceipts,
      residents,
      archivedResidents,
      isLoadingResidents,
      enqueueSnackbarOnce,
    }) => {
      const { t } = useTranslation();
      const theme = useTheme();
      const { fCurrencyCents } = useFormatting();

      const individualDatesModeEnabled = individualDatesMode;

      const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

      const [deleteModalOpen, setDeleteModalOpen] = useState(false);

      const [deleteImagesModalOpen, setDeleteImagesModalOpen] = useState(false);

      const inputRef = useRef<HTMLInputElement | null>(null);

      const { enqueueSnackbar } = useSnackbar();

      const { defaultTaxCode } = useTaxItemsAndDefault();

      const setIndividualDate = (date: Date | undefined) => {
        setReceiptFieldValue(
          'individualDate',
          date ? fromZonedTime(startOfDay(date), 'UTC') : undefined
        );
      };

      const [
        individualDateError,
        individualDateTouched,
        setFieldTouched,
        setFieldError,
      ] = useReceiptBatchStore(
        useShallow((state) => [
          (
            state?.formik?.errors?.receipts?.[index] as FormikErrors<
              ReceiptBatchFormValues['receipts'][number]
            >
          )?.individualDate,
          state.formik?.touched?.receipts?.[index]?.individualDate,
          state.formik?.setFieldTouched,
          state.formik?.setFieldError,
        ])
      );

      const showIndividualDateError = useMemo(() => {
        return (
          Boolean(individualDateError) && individualDateTouched !== undefined
        );
      }, [individualDateError, individualDateTouched]);

      const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
          const acceptedFileTypeRegex =
            ACCEPTED_FILE_TYPES.split(',').join('|');
          const file = e.target.files?.[0];
          if (file) {
            if (file.type.match(acceptedFileTypeRegex)) {
              setOpenMenuActions(null);
              const imageId = await upload(file);
              imageId && addImages([imageId]);
            } else {
              setOpenMenuActions(null);
              enqueueSnackbar(t('receipts.batch.invalid-file-type'), {
                variant: 'error',
              });
            }
          }
        },
        [t, enqueueSnackbar, upload, addImages]
      );

      const openFileInput = useCallback(() => {
        if (inputRef.current) {
          inputRef.current.click();
        }
      }, [inputRef]);

      const handleDeleteRow = useCallback(() => {
        removeRow(index);
        setDeleteModalOpen(false);
        setOpenMenuActions(null);
      }, [index, removeRow]);

      const handleDeleteImages = useCallback(() => {
        setReceiptFieldValue('receiptImageIds', null);
        setDeleteImagesModalOpen(false);
        setOpenMenuActions(null);
      }, [setReceiptFieldValue]);

      const addLineItem = useCallback(() => {
        setReceiptFieldValue('lineItems', [
          ...receipt.lineItems,
          createNewLineItem({
            taxes: defaultTaxCode ? [defaultTaxCode] : undefined,
          }),
        ]);
      }, [setReceiptFieldValue, receipt.lineItems, defaultTaxCode]);

      const removeLineItem = useCallback(
        (lineItemIndex: number) => {
          const lineItems = receipt.lineItems.filter(
            (_, i) => i !== lineItemIndex
          );
          if (lineItems.length === 0) {
            lineItems.push(
              createNewLineItem({
                taxes: defaultTaxCode ? [defaultTaxCode] : undefined,
              })
            );
          }
          setReceiptFieldValue('lineItems', lineItems);
        },
        [receipt.lineItems, setReceiptFieldValue, defaultTaxCode]
      );

      const handleKeyUpEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && e.shiftKey) {
          addLineItem();
          e.preventDefault();
        }
      };

      const [deleteClickedTimes, setDeleteClickedTimes] = useState(0);
      const handleDeleteClick = useCallback(
        (lineItemIndex: number) => {
          // If there is something set, or there are other rows, remove the line item
          if (receipt.lineItems.length > 1) {
            removeLineItem(lineItemIndex);
            return;
          }

          // Otherwise inform the user that on another click, the complete receipt batch will be removed
          if (deleteClickedTimes === 0) {
            removeLineItem(lineItemIndex);
            setDeleteClickedTimes(1);

            // Reset after 2 seconds
            setTimeout(() => {
              setDeleteClickedTimes(0);
            }, 2000);
            return;
          }
          // And remove it on the next click
          removeRow(index);
        },
        [
          deleteClickedTimes,
          index,
          receipt.lineItems,
          removeLineItem,
          removeRow,
        ]
      );

      const [maxAmountAutoGenReceiptsError, setMaxAmountAutoGenReceiptsError] =
        useState<undefined | string>(undefined);

      useEffect(() => {
        if (autoGeneratedReceipts) {
          const totalEntryAmount = receipt.lineItems.reduce(
            (acc, lineItem) => acc + (lineItem.amount || 0),
            0
          );
          if (
            totalEntryAmount >
            MAX_ENTRY_TOTAL_AMOUNT_FOR_RECEIPT_GENERATION_IN_CENTS
          ) {
            setMaxAmountAutoGenReceiptsError(
              t('receipts.batch.form.amount-max-receipt-gen', {
                value: fCurrencyCents(
                  MAX_ENTRY_TOTAL_AMOUNT_FOR_RECEIPT_GENERATION_IN_CENTS
                ),
              })
            );
          } else {
            setMaxAmountAutoGenReceiptsError(undefined);
          }
        } else {
          setMaxAmountAutoGenReceiptsError(undefined);
        }
      }, [
        autoGeneratedReceipts,
        fCurrencyCents,
        index,
        receipt.lineItems,
        setFieldError,
        setFieldTouched,
        setMaxAmountAutoGenReceiptsError,
        t,
      ]);

      return (
        <Stack>
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            sx={{
              mb: 1,
              mt: index === 0 ? 0 : 2.4,
              minHeight: rowHeight,
            }}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{
                minWidth: 30,
                minHeight: rowHeight - 12,
              }}
              paddingY={4}
            >
              <Typography fontWeight={700} fontSize={18}>
                #{index + 1}
              </Typography>
            </Stack>

            <Box
              sx={{ width: individualDatesModeEnabled ? 230 : 300 }}
              paddingY={2}
            >
              <TooltipWrap
                setShowTabTooltip={setShowTabTooltip}
                showTabTooltip={showTabTooltip}
              >
                <div>
                  <ServiceProviderReceiptBatchSelectResident
                    receipt={receipt}
                    isLoadingResidents={isLoadingResidents}
                    setReceiptFieldValue={setReceiptFieldValue}
                    enqueueSnackbarOnce={enqueueSnackbarOnce}
                    index={index}
                    archivedResidents={archivedResidents}
                    residents={residents}
                    onKeyUp={handleKeyUpEvent}
                  />
                </div>
              </TooltipWrap>
            </Box>

            {individualDatesModeEnabled && (
              <Box
                sx={{
                  width: 200,
                  pt: 2,
                }}
              >
                <DatePicker
                  label={t('common.date')}
                  value={receipt.individualDate ?? null}
                  maxDate={new Date()}
                  onChange={(value) => {
                    setIndividualDate(value ?? undefined);
                  }}
                  slotProps={{
                    textField: {
                      name: `receipts.${index}.individualDate`,
                      fullWidth: true,
                      variant: 'standard',
                      InputLabelProps: {
                        shrink: true,
                      },
                      inputProps: {
                        placeholder: '',
                      },
                      onBlur: () => {
                        setFieldTouched?.(
                          `receipts.${index}.individualDate`,
                          true
                        );
                      },
                      error: showIndividualDateError,
                      helperText: showIndividualDateError
                        ? individualDateError
                        : undefined,
                    },
                  }}
                />
              </Box>
            )}

            <Stack direction="column" alignItems="flex-start" gap={1}>
              <Stack
                direction="column"
                sx={{
                  backgroundColor: theme.palette.grey['200'],
                  borderRadius: 2,
                }}
              >
                {receipt.lineItems?.map((lineItem, lineItemIndex) => (
                  <ServiceProviderReceiptBatchLineItemRow
                    key={`lineItemRow-${receipt.id}-${lineItem._internalId}`}
                    index={index}
                    lineItemIndex={lineItemIndex}
                    lineItem={lineItem}
                    setLineItemFieldValue={setLineItemFieldValue}
                    setLineItemValues={setLineItemValues}
                    showTabTooltip={showTabTooltip}
                    lastInputRef={
                      lineItemIndex === receipt.lineItems.length - 1
                        ? lastInputRef
                        : undefined
                    }
                    setShowTabTooltip={setShowTabTooltip}
                    lineItemConfigurations={lineItemConfigurations}
                    handleKeyUpEvent={handleKeyUpEvent}
                    showDeleteTooltip={deleteClickedTimes > 0}
                    handleDeleteClick={handleDeleteClick}
                  />
                ))}
              </Stack>
              <Stack width="100%" justifyContent="flex-end">
                {maxAmountAutoGenReceiptsError && (
                  <FormHelperText sx={{ m: 0, ml: 1, color: 'error.main' }}>
                    {maxAmountAutoGenReceiptsError}
                  </FormHelperText>
                )}
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  sx={{
                    mt: 1,
                  }}
                >
                  <Button
                    disabled={receipt.lineItems.length >= 10}
                    variant="text"
                    size="small"
                    onClick={addLineItem}
                    startIcon={<Iconify icon={'ic:baseline-plus'} />}
                    tabIndex={-1}
                  >
                    {t('receipts.batch.form.add-line-item')}
                  </Button>
                  {receipt.lineItems.length > 1 && (
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      fontWeight={600}
                    >
                      {t('receipts.batch.form.entry-total')}:{' '}
                      {fCurrencyCents(
                        receipt.lineItems.reduce(
                          (sum, lineItem) => sum + (lineItem.amount ?? 0),
                          0
                        )
                      )}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>

            <Stack
              justifyContent="center"
              alignItems="center"
              direction="row"
              spacing={1}
              paddingY={2.5}
            >
              {receipt.receiptImageIds && receipt.receiptImageIds.length > 0 ? (
                <Tooltip
                  title={t('receipts.batch.form.receipt-image-available')}
                >
                  <IconButton
                    onClick={() => {
                      setPreviewImageIds(receipt.receiptImageIds ?? []);
                      setShownImageId(
                        receipt.receiptImageIds
                          ? receipt.receiptImageIds[0]
                          : undefined
                      );
                    }}
                    tabIndex={-1}
                  >
                    <Badge
                      badgeContent={receipt.receiptImageIds.length}
                      invisible={receipt.receiptImageIds.length < 2}
                      color="info"
                      sx={{
                        '& .MuiBadge-badge': { fontSize: 12 },
                        position: 'absolute',
                        right: 13,
                        bottom: 11,
                      }}
                    />
                    <Iconify
                      icon="mdi:receipt-text"
                      color={theme.palette.success.main}
                      fontSize={35}
                    />
                  </IconButton>
                </Tooltip>
              ) : autoGeneratedReceipts ? (
                <Tooltip
                  title={t('receipts.batch.one-receipt-will-be-auto-generated')}
                >
                  <IconButton tabIndex={-1}>
                    <Iconify
                      icon="mdi:receipt-text"
                      color={theme.palette.info.main}
                      fontSize={35}
                    />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip
                  title={t('receipts.batch.form.receipt-image-unavailable')}
                >
                  <IconButton tabIndex={-1}>
                    <Iconify
                      icon="mdi:receipt-text-remove"
                      color={theme.palette.grey['400']}
                      fontSize={35}
                    />
                  </IconButton>
                </Tooltip>
              )}

              <input
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <TableMoreMenu
                iconButtonProps={{ tabIndex: -1 }}
                open={openMenu}
                onOpen={() =>
                  setOpenMenuActions(document.activeElement as HTMLElement)
                }
                onClose={() => setOpenMenuActions(null)}
                actions={
                  <>
                    <MenuItem onClick={() => openFileInput()}>
                      <Iconify icon={'ri:image-add-line'} />{' '}
                      {t('receipts.batch.form.add-image')}
                    </MenuItem>
                    <MenuItem
                      sx={{ color: 'error.main' }}
                      onClick={() => setDeleteImagesModalOpen(true)}
                      disabled={!receipt.receiptImageIds?.length}
                    >
                      <Iconify icon={'mdi:receipt-text-remove-outline'} />{' '}
                      {t('receipts.batch.form.delete-images')}
                    </MenuItem>
                    <MenuItem
                      sx={{ color: 'error.main' }}
                      onClick={() => setDeleteModalOpen(true)}
                    >
                      <Iconify icon={'eva:trash-2-outline'} />{' '}
                      {t('receipts.batch.form.delete-entry')}
                    </MenuItem>
                  </>
                }
              />
            </Stack>

            <DeleteDialog
              open={deleteImagesModalOpen}
              handleDelete={handleDeleteImages}
              setDeleteDialogOpen={setDeleteImagesModalOpen}
              confirmationMessage={t(
                'receipts.batch.form.delete-images-confirmation-text'
              )}
            />
            <DeleteDialog
              open={deleteModalOpen}
              handleDelete={handleDeleteRow}
              setDeleteDialogOpen={setDeleteModalOpen}
              confirmationMessage={t(
                'receipts.batch.form.delete-confirmation-text'
              )}
            />
          </Stack>
          <Divider
            sx={{
              marginY: 3,
              borderStyle: 'dashed',
            }}
          />
        </Stack>
      );
    }
  );

interface ServiceProviderReceiptBatchLineItemRowProps {
  index: number;
  lineItemIndex: number;
  lineItem: BatchReceiptDetails['lineItems'][number];
  setShowTabTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  setLineItemValues: (
    lineItemIndex: number,
    name: string,
    amount: number | undefined,
    tax: TaxItem | undefined
  ) => void;
  showTabTooltip: boolean;
  setLineItemFieldValue: (
    field: string,
    lineItemIndex: number,
    value: any
  ) => void;
  lastInputRef?: React.RefObject<HTMLInputElement | null>;
  lineItemConfigurations: LineItemConfigurationResultDto[] | undefined;
  handleKeyUpEvent: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  showDeleteTooltip: boolean;
  handleDeleteClick: (lineItemIndex: number) => void;
}

export const ServiceProviderReceiptBatchLineItemRow: FC<ServiceProviderReceiptBatchLineItemRowProps> =
  memo(
    ({
      index,
      lineItemIndex,
      lineItem,
      setLineItemFieldValue,
      setLineItemValues,
      lastInputRef,
      showTabTooltip,
      setShowTabTooltip,
      lineItemConfigurations,
      handleKeyUpEvent,
      showDeleteTooltip,
      handleDeleteClick,
    }) => {
      const { t } = useTranslation();
      const theme = useTheme();

      // This ref is used by the MoneyInput field to check if the element is still focused.
      // Without it re-rendering this and re-creating the MoneyInput will mean that it won't know the element is still
      // focused and update the string value once it changes.
      // This would lead to weird input behavior, where for instance typing "1" results in "1.00" being entered.
      const amountInputRef = useRef<HTMLInputElement | null>(null);

      const setAmount = useCallback(
        (amount: number | undefined) => {
          setLineItemFieldValue('amount', lineItemIndex, amount);
        },
        [lineItemIndex, setLineItemFieldValue]
      );

      const setName = useCallback(
        (name: string | undefined) => {
          setLineItemFieldValue('name', lineItemIndex, name);
        },
        [lineItemIndex, setLineItemFieldValue]
      );

      const setTax = useCallback(
        (tax: TaxItem | undefined) => {
          setLineItemFieldValue(
            'taxes',
            lineItemIndex,
            tax ? [tax] : undefined
          );
        },
        [lineItemIndex, setLineItemFieldValue]
      );

      const [generateReceipts] = useReceiptBatchStore(
        useShallow((state) => [state.formik?.values?.generateReceipts])
      );

      const { taxItems } = useTaxItemsAndDefault();

      return (
        <Stack direction="row" gap={2} padding={2}>
          <Stack direction="column" gap={1}>
            <TooltipWrap
              setShowTabTooltip={setShowTabTooltip}
              showTabTooltip={showTabTooltip}
            >
              <div>
                <ServiceProviderReceiptBatchServiceSelection
                  lineItemConfigurations={lineItemConfigurations}
                  lineItem={lineItem}
                  setAmount={setAmount}
                  setName={setName}
                  setTax={setTax}
                  lineItemIndex={lineItemIndex}
                  setLineItemValues={setLineItemValues}
                  index={index}
                  textFieldProps={{
                    onKeyUp: handleKeyUpEvent,
                    autoFocus: !lineItem.name && lineItemIndex > 0,
                  }}
                />
              </div>
            </TooltipWrap>

            {generateReceipts && (
              <ServiceProviderReceiptBatchTaxSelection
                index={index}
                lineItemIndex={lineItemIndex}
                taxItems={taxItems}
                handleTaxItemChange={setTax}
                // Todo: We don't yet support multiple taxes
                selectedTaxItem={
                  lineItem.taxes && lineItem.taxes.length === 1
                    ? lineItem.taxes[0]
                    : undefined
                }
              />
            )}
          </Stack>
          <TooltipWrap
            setShowTabTooltip={setShowTabTooltip}
            showTabTooltip={showTabTooltip}
          >
            <div>
              <ReceiptBatchAmount
                index={index}
                lineItemIndex={lineItemIndex}
                amount={lineItem.amount}
                setAmount={setAmount}
                inputRef={lastInputRef ?? amountInputRef}
                onKeyUp={handleKeyUpEvent}
              />
            </div>
          </TooltipWrap>
          <Tooltip
            title={t('receipts.batch.form.delete-receipt')}
            open={showDeleteTooltip}
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  textAlign: 'center',
                  paddingX: 0.8,
                  backgroundColor: theme.palette.error.main,
                  '& .MuiTooltip-arrow': {
                    color: theme.palette.error.main,
                  },
                },
              },
            }}
          >
            <Stack
              sx={{
                marginX: -1,
                mt: 1.5,
              }}
            >
              <IconButton
                onClick={() => handleDeleteClick(lineItemIndex)}
                tabIndex={-1}
              >
                <Iconify
                  icon={'eva:trash-2-outline'}
                  width={20}
                  height={20}
                  color={theme.palette.error.main}
                />
              </IconButton>
            </Stack>
          </Tooltip>
        </Stack>
      );
    }
  );

interface ReceiptBatchAmountProps {
  index: number;
  lineItemIndex: number;
  amount: number | undefined;
  setAmount: (amount: number | undefined) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const ReceiptBatchAmount: FC<ReceiptBatchAmountProps> = memo(
  ({ index, lineItemIndex, amount, setAmount, inputRef, onKeyUp }) => {
    const { t } = useTranslation();

    const [error, touched, setFieldTouched] = useReceiptBatchStore(
      useShallow((state) => [
        (
          (
            state?.formik?.errors?.receipts?.[index] as FormikErrors<
              ReceiptBatchFormValues['receipts'][number]
            >
          )?.lineItems?.[lineItemIndex] as FormikErrors<
            ReceiptBatchFormValues['receipts'][number]['lineItems'][number]
          >
        )?.amount,
        state.formik?.touched?.receipts?.[index]?.lineItems?.[lineItemIndex]
          ?.amount,
        state.formik?.setFieldTouched,
      ])
    );

    const showError = useMemo(() => {
      return Boolean(error) && touched !== undefined;
    }, [error, touched]);

    return (
      <MoneyInput
        name={`receipts.${index}.lineItems.${lineItemIndex}.amount`}
        variant="standard"
        label={t('common.amount')}
        value={amount}
        maxValue={MAX_SERVICE_PROVIDER_STRIPE_PAYMENT_CENT_AMOUNT}
        onChange={setAmount}
        onWheel={(e) => (e.target as HTMLElement).blur()}
        sx={{
          width: 180,
        }}
        error={showError}
        onBlur={() =>
          setFieldTouched?.(
            `receipts.${index}.lineItems.${lineItemIndex}.amount`
          )
        }
        helperText={showError ? error : undefined}
        inputRef={inputRef}
        InputLabelProps={{
          sx: {
            backgroundColor: 'transparent',
            top: -3,
          },
          shrink: true,
        }}
        onKeyUp={onKeyUp}
      />
    );
  }
);

interface TooltipWrapProps {
  children: React.ReactElement<any, any>;
  showTabTooltip: boolean;
  setShowTabTooltip: React.Dispatch<React.SetStateAction<boolean>>;
}

const TooltipWrap: FC<TooltipWrapProps> = ({
  children,
  showTabTooltip,
  setShowTabTooltip,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <Tooltip
      title={t('receipts.batch.form.hint-tab-line-items')}
      disableHoverListener={true}
      disableTouchListener={true}
      disableFocusListener={true}
      onFocus={() => {
        if (showTabTooltip) {
          setOpen(true);
        }
      }}
      onBlur={() => {
        setShowTabTooltip(false);
        setOpen(false);
      }}
      arrow={true}
      placement="top"
      open={open}
      slotProps={{
        tooltip: {
          sx: {
            textAlign: 'center',
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};
